"use client";

import { Text } from "../atoms/text";

import {
  ChevronDownIcon,
  ChevronLeftIcon,
  LockIcon,
  MenuIcon,
  MonitorIcon,
  MoonIcon,
  SunIcon,
} from "lucide-react";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Separator,
  SegmentedControl,
  SegmentedControlList,
  SegmentedControlTrigger,
} from "../atoms";
import { Button } from "../atoms/button";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "../atoms/collapsible";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "../atoms/dropdown-menu";
import { Tooltip, TooltipContent, TooltipTrigger } from "../atoms/tooltip";
import { useTheme, type ThemePreference } from "../hooks";
import { cn } from "../lib/utils";
import {
  foldedUnseen,
  isItemActive,
  matchesPath,
  navItemKey,
  navItemSignature,
  packNavItems,
  packResultsEqual,
  type AppBarNavDropdownItem,
  type AppBarNavItem,
  type PackResult,
} from "./app-bar-pack";
import { UserArea } from "./user-area";
import { UserDropdown } from "./user-dropdown";

export type AppBarProps = React.ComponentProps<typeof Collapsible> & {
  logo: React.ElementType<{ className?: string }>;
  title: string;
  subtitle?: string;
  badge?: React.ReactNode;
  adminBadge?: React.ReactNode;
  backOnClick?: () => void;
  titleHref?: string;
  // Destination for the logo. Defaults to `titleHref` so single-context apps
  // keep one click target, but consumers can point the logo at the app root
  // (e.g. a rounds list) while the title links to the current context.
  logoHref?: string;
  pathname: string;
  navLinks: AppBarNavItem[];
  link: React.ElementType<{
    href: string;
    className?: string;
    onClick?: () => void;
  }>;
  actions?: React.ReactNode;
  user?: {
    name?: string | null;
    email?: string | null;
    image?: string | null;
  };
  userNavLinks?: {
    label: string;
    href: string;
    icon?: React.ElementType;
  }[];
  onSignOut?: () => void;
  // Subdued content rendered at the bottom of the account menu (e.g. a
  // dev-only build/branch badge). Passed straight through to UserDropdown.
  userMenuFooter?: React.ReactNode;
};

function useNavCollision({
  navLinks,
  activeKey,
  hasAdminBadge,
}: {
  navLinks: AppBarNavItem[];
  activeKey: string | null;
  hasAdminBadge: boolean;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const measurerRef = useRef<HTMLDivElement>(null);
  const moreMeasurerRef = useRef<HTMLSpanElement>(null);
  const badgeMeasurerRef = useRef<HTMLSpanElement>(null);

  const [result, setResult] = useState<PackResult>(() => ({
    inline: navLinks,
    folded: [],
    adminFolded: false,
    minInlineWidth: 0,
  }));

  // The measurement inputs live in refs so `recompute` (and the effect
  // below) never depend on the caller's array IDENTITY — SiteHeader-style
  // consumers rebuild navLinks every render, and during an upstream render
  // storm (rapid route changes feeding store emissions) an identity-keyed
  // layout effect re-fires per render. Each re-fire dispatches a setState,
  // and React counts every dispatch toward its 50-nested-update limit even
  // when the state bails unchanged — which crashed whole pages with error
  // #185 (SP-2649). The effect instead keys on a CONTENT signature, so
  // equivalent-content rebuilds fire nothing at all.
  const inputsRef = useRef({ navLinks, activeKey, hasAdminBadge });
  // Published in the COMMIT phase, not during render: a concurrent render
  // that supplies new nav content and is then suspended or abandoned never
  // commits, but a render-time write would still leave its inputs in the
  // ref — and a ResizeObserver firing for the displayed bar would then pack
  // uncommitted items against committed DOM measurements. Layout effects
  // run only for committed renders, and this one is ordered before the
  // recompute effect below (same component, declaration order), so the
  // callback always reads the inputs of the tree it is measuring.
  useLayoutEffect(() => {
    inputsRef.current = { navLinks, activeKey, hasAdminBadge };
  });
  const inputSignature = useMemo(
    () =>
      JSON.stringify([
        navLinks.map(navItemSignature),
        activeKey,
        hasAdminBadge,
      ]),
    [navLinks, activeKey, hasAdminBadge],
  );

  const recompute = useCallback(() => {
    const container = containerRef.current;
    const measurer = measurerRef.current;

    if (!container || !measurer) {
      return;
    }

    const { navLinks, activeKey, hasAdminBadge } = inputsRef.current;

    const widths = new Map<string, number>();

    for (const child of measurer.children) {
      if (!(child instanceof HTMLElement)) {
        continue;
      }

      const key = child.dataset.navKey;

      if (key) {
        widths.set(key, child.offsetWidth);
      }
    }

    setResult((prev) => {
      const next = packNavItems({
        containerW: container.clientWidth,
        navLinks,
        widths,
        activeKey,
        moreBtnW: moreMeasurerRef.current?.offsetWidth ?? 0,
        badgeW: hasAdminBadge
          ? (badgeMeasurerRef.current?.offsetWidth ?? 0)
          : 0,
        prevAdminFolded: prev.adminFolded,
      });
      // Bail to the previous state when the pack is unchanged. This setState
      // runs from a layout effect whose deps include the caller's navLinks
      // array — commonly rebuilt every parent render — so every parent
      // re-render re-fires the effect. Returning a fresh identity each time
      // makes each of those a NESTED update; under an upstream render storm
      // (rapid route changes feeding store emissions) the chain crossed
      // React's 50-nested-update limit and crashed whole pages with error
      // #185 (SP-2649). Object.is on the returned prev stops the cascade.
      return packResultsEqual(prev, next) ? prev : next;
    });
  }, []);

  useLayoutEffect(() => {
    const container = containerRef.current;

    if (!container) {
      return;
    }

    // Synchronous first pass before paint — ResizeObserver fires async, so
    // without this the first frame renders with initial state (all inline,
    // no More, minInlineWidth: 0) and flashes to the collapsed layout one
    // frame later.
    recompute();

    if (typeof ResizeObserver === "undefined") {
      return;
    }

    const observer = new ResizeObserver(() => recompute());
    observer.observe(container);
    return () => observer.disconnect();
  }, [recompute, inputSignature]);

  // Render the CURRENT item objects in the packed layout. The state above
  // deliberately keeps its previous identity when packing is unchanged (see
  // the bail in recompute), so its arrays can hold stale item objects — and
  // a parent that swapped only an onActivate closure would otherwise have
  // desktop links invoking the old handler while the mobile drawer (which
  // renders straight from navLinks) uses the new one. Projecting by key at
  // render keeps handlers and flags fresh without any extra state update.
  const projected = useMemo(() => {
    const byKey = new Map(navLinks.map((item) => [navItemKey(item), item]));
    const project = (items: AppBarNavItem[]) =>
      items.map((item) => byKey.get(navItemKey(item)) ?? item);
    return { inline: project(result.inline), folded: project(result.folded) };
  }, [navLinks, result]);

  return {
    containerRef,
    measurerRef,
    moreMeasurerRef,
    badgeMeasurerRef,
    ...result,
    ...projected,
  };
}

export function AppBar({
  className,
  logo: Logo,
  title,
  subtitle,
  badge,
  adminBadge,
  backOnClick,
  titleHref = "/",
  logoHref,
  pathname,
  navLinks,
  link: Link,
  actions,
  user,
  userNavLinks,
  onSignOut,
  userMenuFooter,
  ...rest
}: AppBarProps) {
  const [open, setOpen] = useState(false);
  const { themePreference, setThemePreference } = useTheme();

  // Close the mobile drawer whenever the route changes, so tapping a nav link
  // inside the fixed `md:hidden` drawer doesn't leave it covering the new page.
  // The drawer is a controlled Collapsible and nothing else resets `open` on
  // navigation. Keyed on pathname so opening a nested action menu (which
  // doesn't change the route) leaves the drawer untouched.
  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  // Close it when the viewport grows past `md`, too: the open drawer is only
  // CSS-hidden there (`md:hidden`), so without this a widen → narrow round
  // trip resurfaces a drawer nobody re-opened — the stranded-latch failure
  // mode (an open flag outliving its rendered surface).
  useEffect(() => {
    const mdViewport = window.matchMedia("(min-width: 768px)");

    const closeOnDesktop = () => {
      if (mdViewport.matches) {
        setOpen(false);
      }
    };

    closeOnDesktop();
    mdViewport.addEventListener("change", closeOnDesktop);
    return () => mdViewport.removeEventListener("change", closeOnDesktop);
  }, []);

  const activeKey = useMemo(() => {
    const item = navLinks.find((i) => isItemActive(pathname, i));
    return item ? navItemKey(item) : null;
  }, [pathname, navLinks]);

  const {
    containerRef,
    measurerRef,
    moreMeasurerRef,
    badgeMeasurerRef,
    inline,
    folded,
    adminFolded,
    minInlineWidth,
  } = useNavCollision({
    navLinks,
    activeKey,
    hasAdminBadge: adminBadge != null,
  });

  const bar = (
    <Collapsible
      // `fixed` (not `sticky`) so the bar is glued to the viewport and never
      // rides the elastic overscroll/rubber-band at the top of the page — an
      // in-flow `sticky` header gets pulled down on an upward overscroll. The
      // spacer below reserves the bar's height in flow (it's the same height
      // including any `pt-sa-*` safe-area padding consumers add).
      className={cn("fixed top-0 inset-x-0 z-50 bg-surface-base", className)}
      open={open}
      onOpenChange={setOpen}
      render={<nav />}
      {...rest}
    >
      <div className="h-16 px-4 flex items-center justify-between gap-4">
        {backOnClick && (
          <button
            type="button"
            aria-label="Back"
            onClick={backOnClick}
            className="inline-flex items-center text-muted-foreground hover:text-foreground transition-colors shrink-0"
          >
            <ChevronLeftIcon className="size-7" />
          </button>
        )}
        <div className="flex items-center gap-3 min-w-0">
          {/* Logo links to its own destination (e.g. the app root / rounds
                list) so it stays a distinct target from the title, which
                points at the current context. Falls back to titleHref. */}
          <Link
            className="shrink-0"
            href={logoHref ?? titleHref}
            aria-label="Home"
          >
            <Logo className="size-8 shrink-0" />
          </Link>
          <Link
            className="flex items-center gap-3 min-w-0"
            href={titleHref}
            aria-label={title}
          >
            {subtitle ? (
              <div className="flex flex-col leading-tight min-w-0">
                <Text as="span" variant="heading-md" className="truncate">
                  {title}
                </Text>
                <Text
                  as="span"
                  variant="label-sm"
                  tone="muted-foreground"
                  className="uppercase tracking-wide truncate"
                >
                  {subtitle}
                </Text>
              </div>
            ) : (
              <Text as="span" variant="heading-md" className="truncate">
                {title}
              </Text>
            )}
            {badge}
            {adminBadge && !adminFolded && (
              <span className="hidden md:inline-flex shrink-0">
                {adminBadge}
              </span>
            )}
          </Link>
        </div>

        <div
          ref={containerRef}
          style={{ minWidth: minInlineWidth }}
          className="flex-1 hidden md:flex items-center gap-3 overflow-hidden"
        >
          {inline.map((item) => (
            <InlineNavItem
              key={navItemKey(item)}
              item={item}
              active={isItemActive(pathname, item)}
              link={Link}
            />
          ))}
          {folded.length > 0 && (
            <DropdownMenu>
              <DropdownMenuTrigger
                render={
                  <MoreTrigger
                    className="shrink-0"
                    unseen={foldedUnseen(folded)}
                  />
                }
              />
              <DropdownMenuContent align="start" className="min-w-48">
                {folded.map((item) =>
                  item.kind === "dropdown" ? (
                    // A folded dropdown (e.g. Setup) keeps its grouping as a
                    // nested submenu instead of flattening into the list.
                    <DropdownMenuSub key={navItemKey(item)}>
                      <DropdownMenuSubTrigger className="relative">
                        {item.label}
                        {item.unseen && <NavUnseenDot />}
                      </DropdownMenuSubTrigger>
                      <DropdownMenuSubContent>
                        {item.items.map((leaf) => (
                          <DropdownLeaf
                            key={leaf.href}
                            leaf={leaf}
                            link={Link}
                          />
                        ))}
                      </DropdownMenuSubContent>
                    </DropdownMenuSub>
                  ) : (
                    <DropdownLeaf key={item.href} leaf={item} link={Link} />
                  ),
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>

        <div className="hidden md:flex items-center gap-6 shrink-0">
          {actions}
          {user && (
            <UserDropdown
              user={user}
              adminBadge={adminFolded ? adminBadge : undefined}
              navLinks={userNavLinks}
              link={Link}
              onSignOut={onSignOut}
              footer={userMenuFooter}
            />
          )}
        </div>

        <CollapsibleTrigger className="md:hidden">
          <MenuIcon className="size-8" />
        </CollapsibleTrigger>
      </div>

      {/* Offscreen measurer — same DOM subtree so it inherits styles. */}
      <div
        aria-hidden
        className="absolute pointer-events-none invisible"
        style={{ left: -9999, top: -9999 }}
      >
        <div ref={measurerRef} className="flex items-center gap-3">
          {navLinks.map((item) => (
            <div key={navItemKey(item)} data-nav-key={navItemKey(item)}>
              <InlineNavItem item={item} active={false} link={Link} />
            </div>
          ))}
        </div>
        <span ref={moreMeasurerRef} className="inline-flex">
          <MoreTrigger />
        </span>
        {adminBadge && (
          <span ref={badgeMeasurerRef} className="inline-flex">
            {adminBadge}
          </span>
        )}
      </div>

      <CollapsibleContent
        // Offset the fixed drawer below the visible header: the 4rem row
        // plus --safe-area-top (the floored top inset from styles.css,
        // 0 outside iOS — this collapses to top-16 there).
        // `h-auto` overrides the Collapsible atom's content-height
        // (`h-(--collapsible-panel-height)`), which over-constrains this
        // fixed panel and makes the browser drop `bottom-0`: the drawer ended
        // at its content height, page content peeked out beneath it, and a
        // menu taller than the viewport couldn't scroll (height == content
        // means the panel's own overflow never engages). top+bottom now own
        // the height; the atom's height slide doesn't run, so the drawer
        // appears instantly.
        className="md:hidden fixed inset-x-0 top-[calc(4rem+var(--safe-area-top))] bottom-0 z-50 h-auto overflow-y-auto bg-surface-base border-t border-border-subtle px-4 pb-6 pt-4"
      >
        <div className="flex flex-col gap-6">
          <div className="flex flex-col gap-2">
            {navLinks.flatMap((item) =>
              item.kind === "dropdown"
                ? [
                    <div
                      key={`${item.label}-label`}
                      className="text-xs uppercase tracking-wider text-text-secondary px-3 pt-2"
                    >
                      {item.label}
                    </div>,
                    ...item.items.map((sub) => (
                      <MobileNavLink
                        key={sub.href}
                        link={Link}
                        isActive={matchesPath(pathname, sub.href)}
                        label={sub.label}
                        href={sub.href}
                        badgeCount={sub.badgeCount}
                        disabled={sub.disabled}
                        dimmed={sub.dimmed}
                        unseen={sub.unseen}
                        onActivate={sub.onActivate}
                      />
                    )),
                  ]
                : [
                    <MobileNavLink
                      key={item.href}
                      link={Link}
                      isActive={isItemActive(pathname, item)}
                      label={item.label}
                      href={item.href}
                      badgeCount={item.badgeCount}
                      disabled={item.disabled}
                      dimmed={item.dimmed}
                      unseen={item.unseen}
                      onActivate={item.onActivate}
                    />,
                  ],
            )}
          </div>
          {actions && (
            <>
              <Separator />
              {actions}
            </>
          )}
          {user && userNavLinks && userNavLinks.length > 0 && (
            <>
              <Separator />
              {userNavLinks.map((link) => (
                <MobileNavLink
                  key={link.href}
                  className="text-text-secondary"
                  link={Link}
                  isActive={matchesPath(pathname, link.href)}
                  {...link}
                />
              ))}
            </>
          )}
          <Separator />
          <SegmentedControl
            value={themePreference}
            onValueChange={(value) =>
              setThemePreference(value as ThemePreference)
            }
          >
            <SegmentedControlList className="w-full">
              <SegmentedControlTrigger className="flex-1" value="system">
                <MonitorIcon />
                System
              </SegmentedControlTrigger>
              <SegmentedControlTrigger className="flex-1" value="light">
                <SunIcon />
                Light
              </SegmentedControlTrigger>
              <SegmentedControlTrigger className="flex-1" value="dark">
                <MoonIcon />
                Dark
              </SegmentedControlTrigger>
            </SegmentedControlList>
          </SegmentedControl>
          {user && (
            <>
              <Separator />
              <UserArea className="p-0" user={user} onSignOut={onSignOut} />
            </>
          )}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );

  return (
    <>
      {bar}
      {/* In-flow spacer reserving the fixed bar's height (4rem +
          --safe-area-top) so page content starts below it. */}
      <div
        aria-hidden
        className="h-[calc(4rem+var(--safe-area-top))] shrink-0"
      />
    </>
  );
}

function NavBadgeCount({ count }: { count?: number }) {
  if (count === undefined) {
    return null;
  }

  return (
    <Text
      as="span"
      variant="label-md"
      className="size-5 rounded-full bg-warning/8 flex items-center justify-center text-warning"
    >
      {count}
    </Text>
  );
}

function NavUnseenDot() {
  return (
    <span
      aria-label="Unseen"
      className="absolute top-1 right-1 h-2 w-2 rounded-full bg-blue-500"
    />
  );
}

// Must forward all props (onClick/ref/aria-expanded/etc.) so Radix's
// `<DropdownMenuTrigger asChild>` Slot can wire its trigger behavior onto
// the inner Button. Without spreading `...rest`, the dropdown wouldn't
// open on click.
function MoreTrigger({
  className,
  unseen,
  ...rest
}: React.ComponentProps<typeof Button> & { unseen?: boolean }) {
  return (
    <Button
      variant="nav"
      className={cn("relative border border-transparent", className)}
      {...rest}
    >
      More
      <ChevronDownIcon className="size-4" />
      {unseen && <NavUnseenDot />}
    </Button>
  );
}

function InlineNavItem({
  item,
  active,
  link: Link,
}: {
  item: AppBarNavItem;
  active: boolean;
  link: React.ElementType<{
    href: string;
    className?: string;
    onClick?: () => void;
  }>;
}) {
  const dimmed = item.kind !== "dropdown" && item.dimmed && !item.disabled;
  const className = cn(
    "border border-transparent shrink-0",
    active && "bg-surface-elevated border-border-subtle",
    dimmed && !active && "opacity-60",
  );

  if (item.kind === "dropdown") {
    return (
      <DropdownMenu>
        <DropdownMenuTrigger
          render={
            <Button variant="nav" className={cn(className, "relative")} />
          }
        >
          {item.label}
          <ChevronDownIcon className="size-4" />
          {item.unseen && <NavUnseenDot />}
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="min-w-48">
          {item.items.map((sub) => (
            <DropdownLeaf key={sub.href} leaf={sub} link={Link} />
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  if (item.disabled) {
    const body = (
      <span
        aria-disabled="true"
        className={cn(
          "inline-flex items-center gap-2 rounded-md text-sm font-medium h-9 px-3 text-foreground shrink-0",
          "opacity-40 cursor-not-allowed",
        )}
      >
        {item.label}
        <NavBadgeCount count={item.badgeCount} />
      </span>
    );
    return item.tooltip ? (
      <Tooltip>
        <TooltipTrigger render={body} />
        <TooltipContent>{item.tooltip}</TooltipContent>
      </Tooltip>
    ) : (
      body
    );
  }

  const button = (
    <Button
      variant="nav"
      className={cn(className, "relative")}
      nativeButton={false}
      render={<Link href={item.href} onClick={item.onActivate} />}
    >
      {item.label}
      {/* Touch can't reach the hover tooltip, so a dimmed tab carries a
          visible read-only marker on coarse pointers. Fine pointers are
          untouched. */}
      {dimmed && (
        <LockIcon
          aria-hidden
          className="hidden pointer-coarse:inline size-3 shrink-0"
        />
      )}
      <NavBadgeCount count={item.badgeCount} />
      {item.unseen && <NavUnseenDot />}
    </Button>
  );
  return item.tooltip ? (
    <Tooltip>
      <TooltipTrigger render={button} />
      <TooltipContent>{item.tooltip}</TooltipContent>
    </Tooltip>
  ) : (
    button
  );
}

// Single dropdown-menu leaf. Disabled leaves render as a non-clickable
// `<DropdownMenuItem disabled>` with the tooltip surfaced inline as a
// muted hint on the right (Radix sets pointer-events: none on disabled
// items, so a hover Tooltip wouldn't fire; the inline hint matches the
// pattern in Joel's SetupNavDropdown and works on touch). Dimmed leaves
// are clickable, so they explain themselves with the same hover Tooltip
// as the inline tabs — a faded item with no explanation reads as broken.
// On touch (no hover) the coarse-pointer lock icon still marks them.
// Reused by Setup-as-dropdown and the More overflow.
function DropdownLeaf({
  leaf,
  link: Link,
}: {
  leaf: AppBarNavDropdownItem;
  link: React.ElementType<{
    href: string;
    className?: string;
    onClick?: () => void;
  }>;
}) {
  if (leaf.disabled) {
    return (
      <DropdownMenuItem disabled className="flex items-center gap-2">
        <span>{leaf.label}</span>
        <NavBadgeCount count={leaf.badgeCount} />
        {leaf.tooltip && (
          <span className="ml-auto text-xs text-muted-foreground">
            {leaf.tooltip}
          </span>
        )}
      </DropdownMenuItem>
    );
  }

  const item = (
    <DropdownMenuItem
      className={cn(
        "relative flex items-center gap-2",
        leaf.dimmed && "opacity-60",
      )}
      nativeButton={false}
      render={<Link href={leaf.href} onClick={leaf.onActivate} />}
    >
      {leaf.label}
      <NavBadgeCount count={leaf.badgeCount} />
      {leaf.unseen && <NavUnseenDot />}
      {/* Same coarse-pointer read-only marker as the inline tabs. */}
      {leaf.dimmed && (
        <LockIcon
          aria-hidden
          className="ml-auto hidden pointer-coarse:inline size-3 shrink-0"
        />
      )}
    </DropdownMenuItem>
  );
  return leaf.tooltip ? (
    <Tooltip>
      <TooltipTrigger render={item} />
      <TooltipContent>{leaf.tooltip}</TooltipContent>
    </Tooltip>
  ) : (
    item
  );
}

function MobileNavLink({
  className,
  label,
  href,
  badgeCount,
  icon: Icon,
  isActive,
  disabled,
  dimmed,
  unseen,
  onActivate,
  link: Link,
}: {
  className?: string;
  label: string;
  href: string;
  badgeCount?: number;
  icon?: React.ElementType;
  isActive: boolean;
  disabled?: boolean;
  dimmed?: boolean;
  unseen?: boolean;
  onActivate?: () => void;
  link: React.ElementType<{
    href: string;
    className?: string;
    onClick?: () => void;
  }>;
}) {
  const buttonClassName = cn(
    "justify-start border border-transparent relative",
    isActive && "bg-surface-elevated border-border-subtle",
    disabled && "opacity-40 cursor-not-allowed",
    !disabled && !isActive && dimmed && "opacity-60",
    className,
  );

  if (disabled) {
    return (
      <Button variant="nav" className={buttonClassName} aria-disabled>
        {Icon && <Icon className="size-4" />}
        {label}
        <NavBadgeCount count={badgeCount} />
      </Button>
    );
  }

  return (
    <Button
      variant="nav"
      className={buttonClassName}
      nativeButton={false}
      render={<Link href={href} onClick={onActivate} />}
    >
      {Icon && <Icon className="size-4" />}
      {label}
      <NavBadgeCount count={badgeCount} />
      {unseen && <NavUnseenDot />}
      {dimmed && (
        <LockIcon
          aria-hidden
          className="ml-auto hidden pointer-coarse:inline size-3 shrink-0"
        />
      )}
    </Button>
  );
}
